import styled from '@emotion/styled'
import { createRef, useState } from 'react'
import useValidateImage from './useValidateImage'
import { Button } from '@mui/material'
import SVG from '../../../assets/svg'
import { Cropper, ReactCropperElement } from 'react-cropper'
import { color, size } from '../../../styles'

import 'cropperjs/dist/cropper.css'

interface Props {
   onSaveImage: (image: string) => any
   maxImageSize: number
}

const ImageUploader: React.FC<Props> = ({ onSaveImage, maxImageSize }) => {
   // ======================
   // State & Effects & Refs
   // ======================
   const [imageFile, setImageFile] = useState<File | null>(null)
   const [imageType, setImageType] = useState<string>('image/png')
   const cropperRef = createRef<ReactCropperElement>()

   // Validate image file and get image data url
   const { image, error } = useValidateImage(imageFile, maxImageSize)

   // ========
   // Handlers
   // ========
   // Get a new file after error
   const getNewFile = (e: any) => {
      e.preventDefault()
      let files
      if (e.dataTransfer) {
         files = e.dataTransfer.files
      } else if (e.target) {
         files = e.target.files
      }
      setImageFile(files[0])
      setImageType(files[0].type)
      e.target.value = null
   }

   // Set crop data on save
   const setCropData = () => {
      if (typeof cropperRef.current?.cropper !== 'undefined') {
         onSaveImage(cropperRef.current?.cropper.getCroppedCanvas().toDataURL(imageType))
      }
   }

   const showImageSelector: boolean = !!!image || !!error
   const showCropper: boolean = !!image && !error

   return (
      <Holder>
         {showImageSelector && (
            <div className="image-selector">
               {error ? (
                  <div className="message error">
                     <SVG.form.error />
                     <p>{error}</p>
                  </div>
               ) : (
                  <div className="message info">
                     <SVG.form.uploadToCloud />
                     <p>Please select an image</p>
                  </div>
               )}
               <Button
                  variant="contained"
                  color="info"
                  component="label"
                  startIcon={<SVG.form.uploadArrow />}
               >
                  {error ? 'Try Again' : 'Choose File'}
                  <input
                     type="file"
                     name="image"
                     id=""
                     hidden
                     accept="image/*"
                     onChange={getNewFile}
                  />
               </Button>
            </div>
         )}
         {showCropper && (
            <div className="image-cropper">
               <Cropper
                  ref={cropperRef}
                  restore={true}
                  zoomOnWheel={true}
                  movable={true}
                  center={true}
                  initialAspectRatio={1}
                  aspectRatio={1}
                  rotatable={true}
                  preview=".img-preview"
                  src={image as string}
                  viewMode={3}
                  dragMode="move"
                  background={true}
                  responsive={true}
                  autoCropArea={1}
                  checkOrientation={true}
                  guides={true}
                  cropBoxMovable={false}
                  cropBoxResizable={false}
                  cellPadding={2}
                  controls={true}
                  className="cropper"
                  zoomOnTouch
                  crop={setCropData}
               />
               <div className="buttons-holder">
                  <Button
                     variant="outlined"
                     color="secondary"
                     startIcon={<SVG.form.uploadArrow />}
                     component="label"
                     fullWidth
                  >
                     Replace
                     <input
                        type="file"
                        name="replaceImage"
                        id=""
                        hidden
                        accept="image/*"
                        onChange={getNewFile}
                     />
                  </Button>
                  <Button onClick={setCropData} color="info" variant="outlined" fullWidth>
                     Confirm
                  </Button>
               </div>
            </div>
         )}
      </Holder>
   )
}

const Holder = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   .image-selector {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      gap: 1em;
      border-radius: 1em;
      border: 1px dashed ${color.blue};
      background-color: #f9fcff;
      padding: 2em;
      width: 100%;

      .message {
         display: flex;
         flex-direction: column;
         justify-content: center;
         align-items: center;
         color: ${color.blue};

         svg {
            width: 3em;
            height: 3em;
            fill: currentColor;
            opacity: 0.5;
         }

         p {
            color: inherit;
            font-size: ${size.m};
         }

         &.error {
            color: ${color.red};
         }
      }
   }

   .image-cropper {
      display: flex;
      flex-direction: column;
      gap: 0.5em;
      width: 90%;

      .cropper {
         border-radius: 1em;
         aspect-ratio: 1;
         padding: 0;
         margin: 0;
         overflow: hidden;
         flex: 1fr;
         .cropper-view-box {
            position: relative;
            top: 3px;
            left: 3px;
            width: calc(100% - 6px);
            height: calc(100% - 6px);
            border-radius: 50%;
            outline-width: 3px;
         }
      }

      .buttons-holder {
         display: flex;
         justify-content: space-between;
         gap: 2em;
         display: none;
      }
   }
`

export type { Props as ImageUploaderProps }
export default ImageUploader
